import React from 'react';
import { X, AlertTriangle, ArrowUpCircle } from 'lucide-react';
import { useSettingsModal } from '../../contexts/SettingsModalContext';

export type QuotaType = 'prs' | 'sast_scans' | 'deep_scans' | 'applications';

interface QuotaExceededModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotaType: QuotaType;
  isFreeTier?: boolean;
}

const QUOTA_LABELS: Record<QuotaType, { name: string; exhaustedMessage: string; upgradeMessage: string }> = {
  prs: {
    name: 'Pull Requests',
    exhaustedMessage: "You've used all your PR quota for this billing period.",
    upgradeMessage: 'PR creation is not available on the free plan.',
  },
  sast_scans: {
    name: 'SAST Scans',
    exhaustedMessage: "You've used all your SAST scans for this billing period.",
    upgradeMessage: "You've used all your SAST scans. Upgrade for more.",
  },
  deep_scans: {
    name: 'Deep AI Scans',
    exhaustedMessage: "You've used all your deep scans for this billing period.",
    upgradeMessage: "You've used all your deep scans. Upgrade for more.",
  },
  applications: {
    name: 'Applications',
    exhaustedMessage: "You've reached your application limit.",
    upgradeMessage: "You've reached your application limit. Upgrade to add more.",
  },
};

const QuotaExceededModal: React.FC<QuotaExceededModalProps> = ({
  isOpen,
  onClose,
  quotaType,
  isFreeTier = false,
}) => {
  const { openAccountSettings } = useSettingsModal();

  if (!isOpen) return null;

  const labels = QUOTA_LABELS[quotaType];
  const message = isFreeTier ? labels.upgradeMessage : labels.exhaustedMessage;

  const handleUpgrade = () => {
    onClose();
    openAccountSettings();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-ui-background-secondary rounded-lg p-6 max-w-sm w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-ui-text-primary">
              {labels.name} Limit Reached
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-ui-background-tertiary rounded-lg transition-colors"
          >
            <X size={20} className="text-ui-text-tertiary" />
          </button>
        </div>

        <p className="text-sm text-ui-text-secondary mb-6">
          {message}
        </p>

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-ui-text-secondary border border-ui-border-primary rounded-lg hover:bg-ui-background-tertiary transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleUpgrade}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark rounded-lg transition-colors"
          >
            <ArrowUpCircle className="w-4 h-4" />
            Upgrade Plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuotaExceededModal;
